import React, { useCallback, useEffect, useState } from 'react';
import { Select } from 'antd';
import {
  Search,
  RefreshCw,
  Users,
  Award,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Filter,
  HelpCircle,
} from 'lucide-react';
import { assessmentApi } from '../services/assessmentApi';
import {
  ApiDayItem,
  AssessmentDashboardResponse,
  AssessmentDashboardStudentItem,
} from '../data/types';
import { API_URL_IMAGE } from 'env';

import { DAYS_META } from '../data/questionsData';

// Colors & styles for classifications matching Foundation Checkpoint design
export const CLS_CONFIG: Record<
  string,
  { label: string; fg: string; bg: string; dot: string; border: string }
> = {
  GREEN: {
    label: 'XANH',
    fg: '#1E7A4E',
    bg: '#E3F3EA',
    dot: '#2E9E66',
    border: '#A3E6CD',
  },
  YELLOW: {
    label: 'VÀNG',
    fg: '#8A6400',
    bg: '#FBF1D6',
    dot: '#D9A21B',
    border: '#FCE5A3',
  },
  ORANGE: {
    label: 'CAM',
    fg: '#A4510F',
    bg: '#FCE9DA',
    dot: '#E07B2A',
    border: '#F7C6A5',
  },
  RED: {
    label: 'ĐỎ',
    fg: '#B42A2A',
    bg: '#FBE3E3',
    dot: '#D14343',
    border: '#F8BDBD',
  },
  UNCLASSIFIED: {
    label: 'CHƯA PHÂN LOẠI',
    fg: '#5A6577',
    bg: '#EEF1F4',
    dot: '#8A94A3',
    border: '#D5DBE3',
  },
  NONE: {
    label: '—',
    fg: '#8A94A3',
    bg: '#EEF1F4',
    dot: '#C3CAD4',
    border: '#E2E6EC',
  },
};

const pad = (n: number) => String(n).padStart(2, '0');
const fmtDT = (iso?: string | null) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(
      d.getHours()
    )}:${pad(d.getMinutes())}`;
  } catch {
    return iso || '—';
  }
};

const getInitials = (name?: string) => {
  if (!name) return 'HV';
  return (
    name
      .trim()
      .split(' ')
      .filter(Boolean)
      .slice(-2)
      .map((w) => w[0])
      .join('')
      .toUpperCase() || 'HV'
  );
};

/**
 * Kiểm tra xem user hiện tại trong localStorage có role là 'admin' hoặc 'super_admin' hay không
 */
export function isUserAdmin(): boolean {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return false;
    const user = JSON.parse(raw);
    if (!user) return false;

    // 1. Kiểm tra mảng roles: [{ id: 1, name: "super_admin", ... }]
    if (Array.isArray(user.roles)) {
      const hasAdmin = user.roles.some((r: any) => {
        const name = String(r?.name || '').toLowerCase();
        return name === 'admin' || name === 'super_admin';
      });
      if (hasAdmin) return true;
    }

    // 2. Kiểm tra mảng assigned_roles: [{ id: 1, name: "super_admin", ... }]
    if (Array.isArray(user.assigned_roles)) {
      const hasAdminAssigned = user.assigned_roles.some((r: any) => {
        const name = String(r?.name || '').toLowerCase();
        return name === 'admin' || name === 'super_admin';
      });
      if (hasAdminAssigned) return true;
    }

    // 3. Kiểm tra mảng role_names: ["User", "Super Administrator"]
    if (Array.isArray(user.role_names)) {
      const hasAdminName = user.role_names.some((r: any) => {
        const name = String(r || '').toLowerCase();
        return (
          name.includes('super admin') ||
          name.includes('admin') ||
          name.includes('administrator')
        );
      });
      if (hasAdminName) return true;
    }

    // 4. Kiểm tra chuỗi role đơn (nếu có): user.role
    if (typeof user.role === 'string') {
      const r = user.role.toLowerCase();
      if (r === 'admin' || r === 'super_admin') return true;
    }

    // 5. Kiểm tra roles_display: "User, Super Administrator"
    if (typeof user.roles_display === 'string') {
      const rd = user.roles_display.toLowerCase();
      if (rd.includes('admin') || rd.includes('super_admin') || rd.includes('administrator')) return true;
    }

    return false;
  } catch {
    return false;
  }
}

interface MentorAssessmentDashboardProps {
  apiDays?: ApiDayItem[];
}

export const MentorAssessmentDashboard: React.FC<MentorAssessmentDashboardProps> = ({ apiDays }) => {
  const isAdmin = isUserAdmin();

  const [data, setData] = useState<AssessmentDashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [activeCls, setActiveCls] = useState<string>('ALL');
  const [selectedDay, setSelectedDay] = useState<number | 'ALL'>('ALL');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasChecked, setHasChecked] = useState<boolean>(false);

  const dayOptions = React.useMemo(() => {
    if (Array.isArray(apiDays) && apiDays.length > 0) {
      return apiDays.map((d) => ({
        day: Number(d.day_number || d.id),
      }));
    }
    return DAYS_META.map((m) => ({ day: m.day }));
  }, [apiDays]);

  // Fetch dashboard data from API
  const fetchDashboard = useCallback(
    async (
      searchQuery: string = search,
      clsFilter: string = activeCls,
      page: number = currentPage,
      dayFilter: number | 'ALL' = selectedDay,
      showRefreshIndicator: boolean = false
    ) => {
      // Chỉ user có role admin hoặc super_admin mới được gọi API
      if (!isUserAdmin()) {
        setData(null);
        setIsLoading(false);
        setHasChecked(true);
        return;
      }

      try {
        if (showRefreshIndicator) {
          setIsRefreshing(true);
        } else {
          setIsLoading(true);
        }

        const res = await assessmentApi.getDashboard({
          search: searchQuery || undefined,
          classification:
            clsFilter !== 'ALL' ? [clsFilter] : undefined,
          page,
          day_id: dayFilter !== 'ALL' ? dayFilter : undefined,
        });

        // Validate response structure
        if (res && res.stats && res.students) {
          setData(res);
        } else {
          setData(null);
        }
      } catch (err) {
        // If 403 / 401 or not allowed / no dashboard data, hide section
        console.warn('Assessment dashboard not available or unauthorized:', err);
        setData(null);
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
        setHasChecked(true);
      }
    },
    [search, activeCls, currentPage, selectedDay]
  );

  // Initial load and filter effect
  useEffect(() => {
    if (isAdmin) {
      fetchDashboard(search, activeCls, currentPage, selectedDay);
    }
  }, [fetchDashboard, search, activeCls, currentPage, selectedDay, isAdmin]);

  // Handle search submission
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearch(searchInput.trim());
  };

  // Handle classification filter change
  const handleClsChange = (cls: string) => {
    setCurrentPage(1);
    setActiveCls(cls);
  };

  // Handle day filter change
  const handleDayChange = (day: number | 'ALL') => {
    setCurrentPage(1);
    setSelectedDay(day);
  };

  // 1. Chỉ hiện khi user trong localStorage có role là admin hoặc super_admin
  if (!isAdmin) {
    return null;
  }

  // 2. Nếu đã kiểm tra xong mà không có dữ liệu, ẩn hoàn toàn
  if (hasChecked && !data) {
    return null;
  }

  // During initial check, if not yet checked, don't flash anything
  if (!hasChecked && isLoading) {
    return null;
  }

  // If data exists, render the mentor dashboard section
  if (!data) {
    return null;
  }

  const { stats, students } = data;
  const studentList = students?.data || [];
  const totalStudents = students?.total || 0;
  const lastPage = students?.last_page || 1;

  // Tổng số câu hỏi & tổng điểm tối đa theo API (tổng điểm tối đa = total_questions * 5)
  const totalQuestions = Number(stats?.total_questions ?? 240);
  const maxTotalScore = totalQuestions * 5;

  const greenMin = Math.round(maxTotalScore * 0.85);
  const yellowMin = Math.round(maxTotalScore * 0.70);
  const yellowMax = greenMin - 1;
  const orangeMin = Math.round(maxTotalScore * 0.55);
  const orangeMax = yellowMin - 1;
  const redMin = Math.round(maxTotalScore * 0.20);
  const redMax = orangeMin - 1;

  // Filter chips definitions
  const filterChips: Array<{ key: string; label: string; config?: any }> = [
    { key: 'ALL', label: 'Tất cả' },
    { key: 'GREEN', label: 'Xanh', config: CLS_CONFIG.GREEN },
    { key: 'YELLOW', label: 'Vàng', config: CLS_CONFIG.YELLOW },
    { key: 'ORANGE', label: 'Cam', config: CLS_CONFIG.ORANGE },
    { key: 'RED', label: 'Đỏ', config: CLS_CONFIG.RED },
    { key: 'UNCLASSIFIED', label: 'Chưa phân loại', config: CLS_CONFIG.UNCLASSIFIED },
  ];

  return (
    <section className="fc-mentor-dash" id="mentor-assessment-dashboard">
      {/* Section Header */}
      <div className="fc-mentor-header">
        <div className="fc-mentor-header-info">
          <div className="fc-mentor-scope">
            <span className="fc-mentor-badge">
              TOÀN HỆ THỐNG · ADMIN & MENTOR{selectedDay !== 'ALL' ? ` · NGÀY ${selectedDay}` : ''}
            </span>
          </div>
          <h2 className="fc-mentor-title">Bảng điều khiển Đánh giá Học viên</h2>
          <p className="fc-mentor-sub">
            Theo dõi tiến độ, phân loại năng lực và kết quả đánh giá của học viên
          </p>
        </div>

        <div className="fc-mentor-actions">
          <div className="fc-mentor-day-filter">
            <span className="fc-mentor-day-label">Ngày đánh giá:</span>
            <Select
              className="fc-mentor-day-select"
              value={selectedDay}
              onChange={(val) => handleDayChange(val as number | 'ALL')}
              style={{ width: 120 }}
              options={[
                { value: 'ALL', label: 'Tất cả' },
                ...dayOptions.map((m) => ({
                  value: m.day,
                  label: `Ngày ${m.day}`,
                })),
              ]}
            />
          </div>

          <button
            type="button"
            className={`fc-mentor-refresh-btn ${isRefreshing ? 'spinning' : ''}`}
            onClick={() => fetchDashboard(search, activeCls, currentPage, selectedDay, true)}
            title="Làm mới dữ liệu"
          >
            <RefreshCw size={15} />
            <span>Làm mới</span>
          </button>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="fc-mentor-kpi-grid">
        {/* KPI 1: Số học viên */}
        <div className="fc-kpi-card">
          <div className="fc-kpi-top">
            <span className="fc-kpi-icon-wrap">
              <Users size={14} />
            </span>
            <span className="fc-kpi-label">Số học viên</span>
          </div>
          <div className="fc-kpi-val mono-font">{stats.total_students}</div>
          <div className="fc-kpi-sub">học viên trong hệ thống</div>
        </div>

        {/* KPI 2: Điểm TB tổng */}
        <div className="fc-kpi-card">
          <div className="fc-kpi-top">
            <span className="fc-kpi-icon-wrap">
              <Award size={14} />
            </span>
            <span className="fc-kpi-label">Điểm TB tổng</span>
          </div>
          <div className="fc-kpi-val mono-font">
            {stats.avg_total_score != null ? stats.avg_total_score : '—'}
          </div>
          <div className="fc-kpi-sub">/ {maxTotalScore} điểm tối đa</div>
        </div>

        {/* KPI 3: Tỷ lệ XANH (GREEN) */}
        <div className="fc-kpi-card">
          <div className="fc-kpi-top">
            <span
              className="fc-kpi-dot"
              style={{ background: CLS_CONFIG.GREEN.dot }}
            />
            <span className="fc-kpi-label">Tỷ lệ XANH</span>
          </div>
          <div className="fc-kpi-val mono-font" style={{ color: CLS_CONFIG.GREEN.fg }}>
            {stats.ratio_green ?? 0}%
          </div>
          <div className="fc-kpi-sub">Xanh · {greenMin}–{maxTotalScore} điểm</div>
        </div>

        {/* KPI 4: Tỷ lệ VÀNG (YELLOW) */}
        <div className="fc-kpi-card">
          <div className="fc-kpi-top">
            <span
              className="fc-kpi-dot"
              style={{ background: CLS_CONFIG.YELLOW.dot }}
            />
            <span className="fc-kpi-label">Tỷ lệ VÀNG</span>
          </div>
          <div className="fc-kpi-val mono-font" style={{ color: CLS_CONFIG.YELLOW.fg }}>
            {stats.ratio_yellow ?? 0}%
          </div>
          <div className="fc-kpi-sub">Vàng · {yellowMin}–{yellowMax} điểm</div>
        </div>

        {/* KPI 5: Tỷ lệ CAM (ORANGE) */}
        <div className="fc-kpi-card">
          <div className="fc-kpi-top">
            <span
              className="fc-kpi-dot"
              style={{ background: CLS_CONFIG.ORANGE.dot }}
            />
            <span className="fc-kpi-label">Tỷ lệ CAM</span>
          </div>
          <div className="fc-kpi-val mono-font" style={{ color: CLS_CONFIG.ORANGE.fg }}>
            {stats.ratio_orange ?? 0}%
          </div>
          <div className="fc-kpi-sub">Cam · {orangeMin}–{orangeMax} điểm</div>
        </div>

        {/* KPI 6: Tỷ lệ ĐỎ (RED) */}
        <div className="fc-kpi-card">
          <div className="fc-kpi-top">
            <span
              className="fc-kpi-dot"
              style={{ background: CLS_CONFIG.RED.dot }}
            />
            <span className="fc-kpi-label">Tỷ lệ ĐỎ</span>
          </div>
          <div className="fc-kpi-val mono-font" style={{ color: CLS_CONFIG.RED.fg }}>
            {stats.ratio_red ?? 0}%
          </div>
          <div className="fc-kpi-sub">Đỏ · {redMin}–{redMax} điểm</div>
        </div>

        {/* KPI 7: Cần Đánh giá lại */}
        <div className="fc-kpi-card highlight-danger">
          <div className="fc-kpi-top">
            <span
              className="fc-kpi-dot"
              style={{ background: CLS_CONFIG.RED.dot }}
            />
            <span className="fc-kpi-label">Cần Đánh giá lại</span>
          </div>
          <div className="fc-kpi-val mono-font" style={{ color: CLS_CONFIG.RED.fg }}>
            {stats.need_review ?? 0}
          </div>
          <div className="fc-kpi-sub">🚩 Ưu tiên đánh giá 1:1</div>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="fc-mentor-panel">
        {/* Controls: Search, Classification Chips, Count */}
        <div className="fc-mentor-controls">
          {/* Search bar */}
          <form className="fc-mentor-search-form" onSubmit={handleSearchSubmit}>
            <Search size={16} className="fc-search-icon" />
            <input
              type="text"
              placeholder="Tìm theo tên, email, chức danh..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="fc-mentor-search-input"
            />
            {searchInput && (
              <button
                type="button"
                className="fc-search-clear-btn"
                onClick={() => {
                  setSearchInput('');
                  setSearch('');
                  setCurrentPage(1);
                }}
              >
                ✕
              </button>
            )}
          </form>

          {/* Classification chips */}
          <div className="fc-mentor-chips">
            {filterChips.map((chip) => {
              const isActive = activeCls === chip.key;
              const cfg = chip.config;
              const bg = isActive
                ? chip.key === 'ALL'
                  ? '#0E1A2D'
                  : cfg?.bg || '#E2E6EC'
                : '#FFFFFF';
              const fg = isActive
                ? chip.key === 'ALL'
                  ? '#FFFFFF'
                  : cfg?.fg || '#0E1A2D'
                : '#475569';
              const border = isActive
                ? chip.key === 'ALL'
                  ? '#0E1A2D'
                  : cfg?.border || '#CBD5E1'
                : '#E2E8F0';

              return (
                <button
                  key={chip.key}
                  type="button"
                  className={`fc-chip-btn ${isActive ? 'active' : ''}`}
                  onClick={() => handleClsChange(chip.key)}
                  style={{ background: bg, color: fg, borderColor: border }}
                >
                  {cfg && (
                    <span
                      className="fc-chip-dot"
                      style={{ background: cfg.dot }}
                    />
                  )}
                  <span>{chip.label}</span>
                </button>
              );
            })}
          </div>

          {/* Count Badge */}
          <div className="fc-mentor-count-badge">
            <span className="mono-font">{studentList.length}</span> / {totalStudents} học viên
          </div>
        </div>

        {/* Data Table */}
        <div className="fc-table-responsive">
          <table className="fc-mentor-table">
            <thead>
              <tr>
                <th style={{ minWidth: 230 }}>Học viên</th>
                <th style={{ width: 110, textAlign: 'center' }}>Đã trả lời</th>
                <th style={{ width: 110, textAlign: 'center' }}>Điểm tổng</th>
                <th style={{ width: 130, textAlign: 'center' }}>Hoàn thành</th>
                <th style={{ width: 110, textAlign: 'center' }}>Tỷ lệ điểm</th>
                <th style={{ width: 140, textAlign: 'center' }}>Phân loại</th>
                <th style={{ width: 120, textAlign: 'center' }}>Đánh dấu Mentor</th>
                <th style={{ width: 150, textAlign: 'right' }}>Cập nhật</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="fc-table-loading-td">
                    <div className="fc-table-loading-box">
                      <RefreshCw size={20} className="spinning" />
                      <span>Đang nạp danh sách học viên...</span>
                    </div>
                  </td>
                </tr>
              ) : studentList.length === 0 ? (
                <tr>
                  <td colSpan={8} className="fc-table-empty-td">
                    <div className="fc-table-empty-box">
                      <Filter size={24} />
                      <div className="fc-empty-title">Không tìm thấy học viên phù hợp</div>
                      <div className="fc-empty-sub">
                        Vui lòng thử tìm kiếm với từ khóa khác hoặc thay đổi bộ lọc phân loại.
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                studentList.map((item) => {
                  const u = item.user;
                  const name = u?.fullname || `Học viên #${item.user_id}`;
                  const email = u?.email || '—';
                  const job = u?.job_title;
                  const avatarUrl = u?.avatar
                    ? u.avatar.startsWith('http')
                      ? u.avatar
                      : `${API_URL_IMAGE}/${u.avatar}`
                    : null;
                  const clsCode = String(item.classification || 'UNCLASSIFIED').toUpperCase();
                  const clsInfo = CLS_CONFIG[clsCode] || CLS_CONFIG.UNCLASSIFIED;
                  const isFlagged = Number(item.flag_review_mentor) === 1;
                  const completionPct = parseFloat(String(item.completion_percentage || 0));
                  const scorePct = parseFloat(String(item.score_percentage || 0));

                  return (
                    <tr key={item.id} className="fc-student-row">
                      {/* Student info */}
                      <td>
                        <div className="fc-student-cell">
                          {avatarUrl ? (
                            <img
                              src={avatarUrl}
                              alt={name}
                              className="fc-student-avatar"
                              onError={(e) => {
                                (e.currentTarget as HTMLElement).style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="fc-student-initials">
                              {getInitials(name)}
                            </div>
                          )}
                          <div className="fc-student-details">
                            <div className="fc-student-name" title={name}>
                              {name}
                            </div>
                            <div className="fc-student-meta">
                              {email}
                              {job ? ` · ${job}` : ''}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Total Answered */}
                      <td style={{ textAlign: 'center' }}>
                        <span className="mono-font fc-stat-val">
                          {item.total_answered}
                        </span>
                        <span className="fc-stat-denom">/{totalQuestions}</span>
                      </td>

                      {/* Total Score */}
                      <td style={{ textAlign: 'center' }}>
                        <span className="mono-font fc-score-bold">
                          {item.total_score}
                        </span>
                        <span className="fc-stat-denom">/{maxTotalScore}</span>
                      </td>

                      {/* Completion Percentage */}
                      <td style={{ textAlign: 'center' }}>
                        <div className="fc-progress-cell">
                          <span className="mono-font fc-pct-text">
                            {completionPct.toFixed(1)}%
                          </span>
                          <div className="fc-progress-track">
                            <div
                              className="fc-progress-fill"
                              style={{
                                width: `${Math.min(100, Math.max(0, completionPct))}%`,
                                background: completionPct >= 100 ? '#10B981' : '#3B82F6',
                              }}
                            />
                          </div>
                        </div>
                      </td>

                      {/* Score Percentage */}
                      <td style={{ textAlign: 'center' }}>
                        <span className="mono-font fc-score-pct">
                          {scorePct.toFixed(1)}%
                        </span>
                      </td>

                      {/* Classification Badge */}
                      <td style={{ textAlign: 'center' }}>
                        <span
                          className="fc-cls-badge"
                          style={{
                            background: clsInfo.bg,
                            color: clsInfo.fg,
                            borderColor: clsInfo.border,
                          }}
                        >
                          <span
                            className="fc-cls-dot"
                            style={{ background: clsInfo.dot }}
                          />
                          {clsInfo.label}
                        </span>
                      </td>

                      {/* Mentor Review Flag */}
                      <td style={{ textAlign: 'center' }}>
                        {isFlagged ? (
                          <span className="fc-review-flag active" title="Yêu cầu Mentor đánh giá 1:1">
                            <AlertTriangle size={13} />
                            <span>Cần đánh giá</span>
                          </span>
                        ) : (
                          <span className="fc-review-flag normal">
                            <span>—</span>
                          </span>
                        )}
                      </td>

                      {/* Updated At */}
                      <td style={{ textAlign: 'right' }}>
                        <span className="fc-updated-at mono-font">
                          {fmtDT(item.updated_at || item.created_at)}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        {lastPage > 1 && (
          <div className="fc-mentor-pagination">
            <div className="fc-page-info">
              Trang <span className="mono-font">{currentPage}</span> / <span className="mono-font">{lastPage}</span>
            </div>

            <div className="fc-page-nav">
              <button
                type="button"
                className="fc-page-btn"
                disabled={currentPage <= 1 || isLoading}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft size={16} />
                <span>Trước</span>
              </button>

              <div className="fc-page-numbers">
                {Array.from({ length: lastPage }, (_, i) => i + 1)
                  .filter((p) => {
                    return (
                      p === 1 ||
                      p === lastPage ||
                      Math.abs(p - currentPage) <= 1
                    );
                  })
                  .map((p, idx, arr) => {
                    const prev = arr[idx - 1];
                    const hasGap = prev && p - prev > 1;
                    return (
                      <React.Fragment key={p}>
                        {hasGap && <span className="fc-page-ellipsis">...</span>}
                        <button
                          type="button"
                          className={`fc-page-num ${p === currentPage ? 'active' : ''}`}
                          onClick={() => setCurrentPage(p)}
                        >
                          {p}
                        </button>
                      </React.Fragment>
                    );
                  })}
              </div>

              <button
                type="button"
                className="fc-page-btn"
                disabled={currentPage >= lastPage || isLoading}
                onClick={() => setCurrentPage((p) => Math.min(lastPage, p + 1))}
              >
                <span>Sau</span>
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default MentorAssessmentDashboard;
